import classNames from "classnames";
import { styled } from "@linaria/react";
import React from "react";
import { fonts, zIndex } from "..";
import { color } from "../index";
// import Color from "color";
import { MIN_ROW_HEIGHT } from "./Tr";
import {
  RowSelector,
  ROW_SELECTOR_PADDING_PX,
  ROW_SELECTOR_SIZE_PX,
  SELECTABLE_ROWS_CLASSNAME,
  SELECTABLE_ROW_CHECKBOX_CLASSNAME,
  SELECTED_CLASSNAME,
} from "./useSelectedRows";
import type { JustificationProps } from "./ColumnMetaData";
import TooltipV2 from "../TooltipV2";
import { css } from "@linaria/core";
import { spacings } from "../Theme";

export const INTERACTIVE_CLASSNAME = "interactive";
export const STICKY_BORDER_RIGHT_CLASSNAME = "stickyBorderRight";
export const STICKY_BORDER_LEFT_CLASSNAME = "stickyBorderLeft";
export const VERTICAL_SEPARATOR_CLASSNAME = "verticalSeparator";

const JUSTIFY_LEFT = "justify-left";
const JUSTIFY_CENTER = "justify-center";
const JUSTIFY_RIGHT = "justify-right";

const VERTICAL_ALIGNMENT_CENTER = "vertical-alignment-center";
const TOP_MARGIN = "top-margin";
const EMPTY_MARK_TOP_MARGIN = 6; // 19 is the empty symbol (-) height in navigator

interface P<Data> extends JustificationProps {
  className?: string;
  ellipsis?: boolean;
  render?: (data: Data) => React.ReactNode;
  verticalSeparator?: boolean;
  data: Data;
  onSelection: (() => void) | null;
  isRowSelected: (data: Data) => boolean;
  rowKey: string;
}

export const SCROLL_LEFT_CLASSNAME = "scrollLeft";
export const SCROLL_RIGHT_CLASSNAME = "scrollRight";

interface ContentP<Data> {
  data: Data;
  ellipsis: boolean;
  showEmptyMark: boolean;
  alignment: JustificationProps["alignment"];
  verticalAlignmentCenter: boolean;
  render?: (data: Data) => React.ReactNode;
}

function Content<Data>({
  data,
  ellipsis,
  render,
  showEmptyMark,
  alignment,
  verticalAlignmentCenter,
}: ContentP<Data>) {
  const renderedContent = render?.(data);
  const empty = !renderedContent && renderedContent !== 0 && showEmptyMark;
  const content = empty ? <EmptyMark topMargin={!verticalAlignmentCenter} /> : renderedContent;
  if (!ellipsis || empty) {
    return <>{content}</>;
  }

  return (
    <TooltipV2
      backgroundColor={color.darkNitrogen900}
      openDelay={1000}
      overlay={<Overlay>{content}</Overlay>}
    >
      <Ellipsis
        className={classNames({
          [JUSTIFY_LEFT]: alignment === "left",
          [JUSTIFY_CENTER]: alignment === "center",
          [JUSTIFY_RIGHT]: alignment === "right",
        })}
      >
        {content}
      </Ellipsis>
    </TooltipV2>
  );
}

function Td<Data>({
  className,
  ellipsis = false,
  showEmptyMark = true,
  sticky,
  stickyDirection,
  stickyBorder = true,
  render,
  data,
  verticalSeparator,
  alignment,
  verticalAlignmentCenter = true,
  minWidth,
  maxWidth,
  onSelection,
  isRowSelected,
  width,
  rowKey,
  ...dataAttributes
}: P<Data>) {
  return (
    <td
      className={classNames(tableData, className, {
        sticky: typeof sticky === "number" || sticky === true,
        [stickyDirection && stickyDirection === "right"
          ? STICKY_BORDER_RIGHT_CLASSNAME
          : STICKY_BORDER_LEFT_CLASSNAME]: stickyBorder,
        [VERTICAL_SEPARATOR_CLASSNAME]: verticalSeparator,
        [JUSTIFY_LEFT]: alignment === "left",
        [JUSTIFY_CENTER]: alignment === "center",
        [JUSTIFY_RIGHT]: alignment === "right",
        [VERTICAL_ALIGNMENT_CENTER]: verticalAlignmentCenter,
      })}
      style={React.useMemo(
        () => ({
          [stickyDirection === "right" ? "right" : "left"]:
            typeof sticky === "number" || sticky === true
              ? `${typeof sticky === "number" ? sticky : 0}px`
              : "initial",
          minWidth: minWidth || width || 0,
          maxWidth: maxWidth || width || "initial",
        }),
        [sticky, stickyDirection, minWidth, width, maxWidth],
      )}
      {...dataAttributes}
    >
      {onSelection ? (
        <RowSelector
          smallIndicator
          value={rowKey}
          onClick={onSelection}
          className={SELECTABLE_ROW_CHECKBOX_CLASSNAME}
          defaultChecked={isRowSelected(data)}
        />
      ) : null}
      <Content
        alignment={alignment}
        verticalAlignmentCenter={verticalAlignmentCenter}
        data={data}
        ellipsis={ellipsis}
        showEmptyMark={showEmptyMark}
        render={render}
      />
    </td>
  );
}

export default Td;

const tableData = css`
  position: relative;
  padding: ${spacings.xs};
  flex: 1 1 0;
  min-height: ${MIN_ROW_HEIGHT}px;
  display: flex;
  overflow-wrap: anywhere;
  background-color: ${color.hydrogen};
  font: ${fonts.body1};
  align-items: flex-start;

  &.${VERTICAL_ALIGNMENT_CENTER} {
    align-items: center;
  }

  &.${JUSTIFY_LEFT} {
    justify-content: flex-start;
    text-align: left;
  }

  &.${JUSTIFY_CENTER} {
    justify-content: center;
    text-align: center;
  }

  &.${JUSTIFY_RIGHT} {
    justify-content: flex-end;
    text-align: right;
  }

  /* GOTCHA https://gaurav5430.medium.com/css-flex-positioning-gotchas-child-expands-to-more-than-the-width-allowed-by-the-parent-799c37428dd6 */
  width: 0;

  &.sticky {
    position: sticky;
    z-index: ${zIndex.overPositionedElements};

    &.${STICKY_BORDER_LEFT_CLASSNAME}::before {
      content: "";
      position: absolute;
      width: 3px;
      right: 0;
      top: 0;
      bottom: 0;
      background-color: ${color.titanium100};
      opacity: 0;
      will-change: opacity;
      content-visibility: auto;
    }

    .${SCROLL_LEFT_CLASSNAME} &.${STICKY_BORDER_LEFT_CLASSNAME}::before {
      opacity: 1;
    }

    &.${STICKY_BORDER_RIGHT_CLASSNAME}::before {
      content: "";
      position: absolute;
      width: 3px;
      left: 0;
      top: 0;
      bottom: 0;
      background-color: ${color.titanium100};
      opacity: 0;
      will-change: opacity;
      content-visibility: auto;
    }

    .${SCROLL_RIGHT_CLASSNAME} &.${STICKY_BORDER_RIGHT_CLASSNAME}::before {
      opacity: 1;
    }
  }

  tr:hover.${INTERACTIVE_CLASSNAME} & {
    background-color: ${color.titanium50}; /* FIXME: From hoverColor.toString() */
  }

  tr.${INTERACTIVE_CLASSNAME} & {
    cursor: pointer;
  }

  tr:active.${INTERACTIVE_CLASSNAME} & {
    background-color: ${color.titanium100} !important;
  }

  tr.${SELECTED_CLASSNAME} & {
    background-color: ${color.titanium100} !important;
  }

  .${SELECTABLE_ROWS_CLASSNAME} &:first-of-type {
    padding-left: calc(
      ${ROW_SELECTOR_PADDING_PX}px + ${ROW_SELECTOR_PADDING_PX}px + ${ROW_SELECTOR_SIZE_PX}px
    );
  }

  &.${VERTICAL_SEPARATOR_CLASSNAME}::before {
    content: "";
    position: absolute;
    width: 1px;
    left: 0;
    top: 0;
    bottom: 0;
    background-color: ${color.titanium200};
  }
`;

export const EmptyMarkWrapper = styled.div`
  padding: 0 5px;
  color: ${color.titanium400};
  user-select: none;
  align-self: center;
  &.${TOP_MARGIN} {
    margin-top: ${EMPTY_MARK_TOP_MARGIN}px;
  }
`;

export const EmptyMark = ({ topMargin }: { topMargin?: boolean }) => (
  <EmptyMarkWrapper className={classNames({ [TOP_MARGIN]: topMargin })}>—</EmptyMarkWrapper>
);

const Overlay = styled.div`
  font: ${fonts.body2};
  color: ${color.hydrogen};
`;

const Ellipsis = styled.span`
  &.${JUSTIFY_LEFT} {
    justify-content: flex-start;
    text-align: left;
  }

  &.${JUSTIFY_CENTER} {
    justify-content: center;
    text-align: center;
  }

  &.${JUSTIFY_RIGHT} {
    justify-content: flex-end;
    text-align: right;
  }

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EmptyTd = styled.td`
  padding: 50px 0px;
  width: 100%;
  text-align: center;
`;
